<template>
  <Transition name="slide-fade">
    <div class="agents-list-container" v-show="show">
      <div class="agents-content">
        <div class="agents-header">
          <div class="agents-header-section-left">
            <h2>AI Agents</h2>
            <button @click="goTo('/create-agent')" class="agent-button agent-button-small">
              <i class="fas fa-plus"></i>
              Create
            </button>
          </div>
          <button class="close-button" @click="closeList">×</button>
        </div>
        
        <div v-if="loading" class="agents-list">
          <div class="agent-card">
            <div class="agent-info">
              <div class="loading-avatar"></div>
              <div class="agent-details">
                <div class="loading-text"></div>
                <div class="loading-text-small"></div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else-if="chatAgents.length === 0" class="find-agent-section">
          <span>
            <i class="fas fa-info-circle"></i>
            No chats yet. Start Your First Chat
          </span>
        </div>
        
        <div v-else class="agents-list">
          <div v-for="agent in chatAgents" :key="agent.agentTag" class="agent-item">
            <div class="agent-info">
              <img :src="prepareAvatarUrl(agent.avatar)" :alt="agent.name" class="agent-avatar" />
              <div class="agent-details">
                <h3>{{ agent.name }}</h3>
                <button @click.stop="goTo('/ai-agents/' + agent.agentTag)" class="agent-link-button">{{ agent.agentTag }}</button>
                <small v-if="agent.agentTag !== 'main_agent'" class="agent-last-message-ago">{{ getLastMessageTime(agent.agentTag) }} ago</small>
              </div>
            </div>
            <div class="agent-actions">
              <button 
                class="use-button" 
                @click="selectAgent(agent)"
                :class="{ 'active': isCurrentAgent(agent) }"
              >
                Chat
              </button>
              <button 
                v-if="agent.agentTag !== 'main_agent'"
                class="delete-button" 
                @click="agentToDelete = agent"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>

            <!-- Подтверждение удаления -->
            <Transition name="fade">
              <div v-if="agentToDelete?.agentTag === agent.agentTag" class="delete-confirmation">
                <div class="delete-confirmation-content">
                  <p>Chat history will be deleted and cannot be restored</p>
                  <div class="delete-confirmation-buttons">
                    <button class="delete-confirm-button" @click="deleteAgent(agent); agentToDelete = null">
                      Delete
                    </button>
                    <button class="delete-cancel-button" @click="agentToDelete = null">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        </div>

        <div class="find-agent-section">
          <button @click="goTo('/ai-agents')" class="agent-button">
            <i class="fas fa-rocket"></i>
            Explore AI Agents
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, onMounted, watch, computed, nextTick } from 'vue';
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

const router = useRouter()
const store = useStore()

const props = defineProps({
  isLightTheme: {
    type: Boolean,
    default: false
  }
});

const agentToDelete = ref(null)

const goTo = (path) => {
  store.commit('TOGGLE_CHAT', false)
  router.push(path);
}

const emit = defineEmits(['close', 'select-agent']);

const loading = ref(true)
const chatAgents = computed(() => store.getters.chatAgents)

const show = computed(() => store.getters.agentsListOpen)

const closeList = () => {
  store.commit('TOGGLE_AGENTS_LIST', false)
  emit('close')
}

const prepareAvatarUrl = (avatarNum) => {
  return `https://solanabitcat.com/data/ai-agents/bitcat-ai-agent-avatar-${avatarNum}.png`
}

const isCurrentAgent = (agent) => {
  return store.getters.currentAgent?.agentTag === agent.agentTag
}

const getLastMessageTime = (agentTag) => {
  const history = JSON.parse(localStorage.getItem('chatHistory') || '{}')
  const agentHistory = history[agentTag] || []
  const lastMessageTime = agentHistory[agentHistory.length - 1]?.timestamp
  
  if (!lastMessageTime) return ''
  
  const now = Date.now()
  const messageDate = typeof lastMessageTime === 'string' ? 
    new Date(lastMessageTime).getTime() : lastMessageTime
  
  const diff = now - messageDate
  const minutes = Math.floor(diff / 60000)
  
  if (minutes < 60) {
    return `${minutes || 1}m`
  } else if (minutes < 1440) { // less than 24 hours
    return `${Math.floor(minutes / 60)}h`
  } else {
    return `${Math.floor(minutes / 1440)}d`
  }
}

// Обновляем время каждую минуту
setInterval(() => {
  nextTick()
}, 60000)

const selectAgent = (agent) => {
  store.commit('SELECT_AGENT', agent)
  emit('select-agent', agent)
  emit('close')
}

const sortAgents = (agents) => {
  return agents.sort((a, b) => {
    if (a.agentTag === 'main_agent') return -1
    if (b.agentTag === 'main_agent') return 1
    return 0
  })
}

const deleteAgent = (agent) => {
  if (agent.agentTag === 'main_agent') return // Prevent deletion of main_agent
  store.commit('DELETE_AGENT', agent.agentTag)
}

onMounted(() => {
  store.commit('LOAD_CHAT_AGENTS')
  loading.value = false
})

// Watch for changes in agentInfo
watch(() => store.getters.agentInfo, () => {
  store.commit('LOAD_CHAT_AGENTS')
}, { deep: true })
</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

.agents-list-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--window-bg);
  z-index: 10;
  display: flex;
  flex-direction: column;
}

.agents-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

.agents-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);
}
.agents-header-section-left {
  display: flex;
  gap: 16px;
  align-items: center
}

.agents-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--text-color);
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-color);
  padding: 4px 8px;
}

.agents-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.delete-confirmation {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--window-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border-radius: 12px;
}

.delete-confirmation-content {
  text-align: center;
}

.delete-confirmation-content p {
  font-size: 0.8rem;
  margin: 0 0 12px;
  color: var(--text-color);
}

.delete-confirmation-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.delete-confirm-button,
.delete-cancel-button {
  padding: 4px 12px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: opacity 0.2s;
}

.delete-confirm-button {
  background: #ff4444;
  color: white;
}

.delete-cancel-button {
  background: var(--accent-color);
  color: white;
}

.delete-confirm-button:hover,
.delete-cancel-button:hover {
  opacity: 0.8;
}

/* Анимация появления/исчезновения */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.agent-item {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: var(--message-bg);
  border-radius: 12px;
  border: 1px solid var(--border-color);
}

.agent-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.agent-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.agent-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.agent-details h3 {
  max-width: 160px;
  margin: 0;
  font-size: 1rem;
  color: var(--text-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agent-last-message-ago {
  font-size: 0.8rem;
  color: var(--text-color);
}

.agent-link-button {
  max-width: 150px;
  font-size: 0.9rem;
  color: var(--accent-color);
  border: none;
  background: transparent;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s;
  padding: 0;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.agent-link-button:hover {
  text-decoration: underline;
}

.creator-info {
  display: flex;
  gap: 8px;
  font-size: 0.9rem;
  color: var(--text-color);
}

.use-button {
  background: var(--accent-color);
  color: white;
  border: none;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.use-button:hover {
  filter: brightness(1.1);
}

.use-button.active {
  background: #4CAF50;
}

.agent-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;
}

.delete-button {
  background: transparent;
  border: none;
  color: var(--text-color);
  cursor: pointer;
  margin-bottom: -8px;
  padding: 2px 5px;
  transition: color 0.2s;
}

.delete-button:hover {
  color: #ff4444;
}

.find-agent-section {
  margin-top: 24px;
  display: flex;
  justify-content: center;
}
.find-agent-section span {
  margin: 30px auto;
  color: var(--text-color);
}


.agent-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 2px dashed var(--border-color);
  color: var(--text-color);
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}
.agent-button-small {
  padding: 6px 12px;
  font-size: 0.9rem;
}

.agent-button:hover {
  border-color: var(--accent-color);
  color: var(--accent-color);
}

.loading-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(90deg, var(--border-color) 0%, var(--message-bg) 50%, var(--border-color) 100%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

.loading-text {
  height: 20px;
  width: 150px;
  background: linear-gradient(90deg, var(--border-color) 0%, var(--message-bg) 50%, var(--border-color) 100%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 8px;
}

.loading-text-small {
  height: 16px;
  width: 100px;
  background: linear-gradient(90deg, var(--border-color) 0%, var(--message-bg) 50%, var(--border-color) 100%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
